import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { CognitoService } from './cognito.service';
import { ProgramService } from './program.service';

@Injectable({
  providedIn: 'root'
})
export class RoleChangeService {
  private readonly ROLE_STORAGE_KEY = 'lastKnownUserRole';
  private readonly REDIRECTING_FLAG_KEY = 'roleChangeRedirecting';
  private lastKnownRole: string | null = null;
  private isRedirecting: boolean = false;

  constructor(
    private router: Router,
    private messageService: MessageService,
    private cognitoService: CognitoService,
    private programService: ProgramService
  ) {
    // Load last known role from sessionStorage on service initialization
    this.lastKnownRole = sessionStorage.getItem(this.ROLE_STORAGE_KEY);
  }

  /**
   * Check if user's role has changed and handle the transition gracefully
   * @param currentRoute The route user is trying to access
   * @returns Promise<boolean> - true if access should be allowed
   */
  async handleRoleTransition(currentRoute: string): Promise<boolean> {
    try {
      // Check if we're in the middle of a redirect to avoid loops
      if (this.isRedirecting) {
        this.isRedirecting = false; // Reset flag
        return true;
      }

      const freshRoles = await this.cognitoService.getCurrentUserRoles();
      
      // console.log('=== ROLE TRANSITION DEBUG ===');
      // console.log('Current route:', currentRoute);
      // console.log('Fresh roles from Cognito:', freshRoles);
      // console.log('Last known role (cached):', this.lastKnownRole);
      // console.log('SessionStorage value:', sessionStorage.getItem(this.ROLE_STORAGE_KEY));
      
      // First time checking roles or no cached role
      if (this.lastKnownRole === null) {
        this.setLastKnownRole(freshRoles);
        const allowed = this.isRouteAllowedForRole(currentRoute, freshRoles);
        return allowed;
      }

      // Check if role has changed
      if (this.lastKnownRole !== freshRoles) {
        // console.log('ROLE CHANGE DETECTED!');
        // console.log('Old role:', this.lastKnownRole);
        // console.log('New role:', freshRoles);
        
        // Set redirecting flag before redirect
        this.isRedirecting = true;
        await this.handleRoleChange(this.lastKnownRole, freshRoles, currentRoute);
        this.setLastKnownRole(freshRoles);
        return false; // Block current navigation, redirect happening
      }

      // No role change, check if current route is allowed
      const allowed = this.isRouteAllowedForRole(currentRoute, freshRoles);
      
      // If route not allowed, redirect to appropriate route
      if (!allowed) {
        const redirectRoute = this.getDefaultRouteForRole(freshRoles);
        this.isRedirecting = true;
        await this.router.navigate([redirectRoute]);
        return false;
      }
      
      return allowed;
    } catch (error) {
      return false;
    }
  }

  /**
   * Handle role change by showing notification and redirecting
   */
  private async handleRoleChange(oldRole: string, newRole: string, currentRoute: string): Promise<void> {
    // Reset program service to 0 when role changes
    this.programService.setProgramId(0);
    
    // Show user-friendly notification
    this.showRoleChangeNotification(oldRole, newRole);
    
    // Redirect to appropriate route for new role
    const redirectRoute = this.getDefaultRouteForRole(newRole);
    await this.router.navigate([redirectRoute]);
  }

  /**
   * Show notification about role change
   */
  private showRoleChangeNotification(oldRole: string, newRole: string): void {
    const oldRoleDisplay = this.getRoleDisplayName(oldRole);
    const newRoleDisplay = this.getRoleDisplayName(newRole);
    
    this.messageService.add({
      severity: 'info',
      summary: 'Role Updated',
      detail: `Your role has been changed from ${oldRoleDisplay} to ${newRoleDisplay}. You are being redirected to the appropriate page.`,
      life: 5000
    });
  }

  /**
   * Get user-friendly display name for role
   */
  private getRoleDisplayName(role: string): string {
    if (role.includes('admin')) return 'Super Admin';
    if (role.includes('customer_support')) return 'Customer Support';
    if (role.includes('event_manager') && role.includes('ticket_manager')) return 'Event & Ticket Manager';
    if (role.includes('ticket_manager')) return 'Ticket Manager';
    if (role.includes('ticket_operator')) return 'Ticket Operator';
    if (role.includes('event_manager')) return 'Event Manager';
    return 'User';
  }

  /**
   * Get default route for a given role (matches login component logic)
   */
  private getDefaultRouteForRole(role: string): string {
    if (role.includes('admin')) {
      return '/program-management';
    } else if (role.includes('customer_support')) {
      return '/home';
    } else if (role.includes('event_manager') && role.includes('ticket_manager')) {
      return '/program-management';
    } else if (role.includes('ticket_manager')) {
      return '/home';
    } else if (role.includes('ticket_operator')) {
      return '/home';
    } else if (role.includes('event_manager')) {
      return '/program-management';
    } else {
      return '/home';
    }
  }

  /**
   * Check if a route is allowed for a given role (matches auth guard logic)
   */
  private isRouteAllowedForRole(route: string, role: string): boolean {
    const roleList = role.split(',').map(r => r.trim().toLowerCase());
    const isSuperAdmin = roleList.includes('admin');
    const isCSAdmin = roleList.includes('customer_support');
    const isEventManager = roleList.includes('event_manager');
    const isTicketManager = roleList.includes('ticket_manager');
    const isTicketOperator = roleList.includes('ticket_operator');

    // Super Admin: full access
    if (isSuperAdmin) {
      return true;
    }

    // Customer Support Admin: only issue-related routes
    if (isCSAdmin) {
       const csAllowedRoutes = ['/home', '/issues', '/public-user-issue'];
      return csAllowedRoutes.some(allowedRoute => route.startsWith(allowedRoute));
    }

    // Event Manager + Ticket Manager (combined roles)
    if (isEventManager && isTicketManager) {
      const combinedAllowedRoutes = [
        '/program-management', '/venue-management', '/media-configuration',
        '/content-editor', '/events', '/batch-management', '/ticket-counter'
      ];
      return combinedAllowedRoutes.some(allowedRoute => route.startsWith(allowedRoute));
    }

    // Ticket Manager (standalone)
    if (isTicketManager && !isEventManager) {
      const ticketAllowedRoutes = ['/home', '/events', '/batch-management', '/ticket-counter'];
      return ticketAllowedRoutes.some(allowedRoute => route.startsWith(allowedRoute));
    }

    // Ticket Operator
    if (isTicketOperator) {
      const ticketOperatorAllowedRoutes = ['/home', '/', '/events/event-ticket', '/report', '/events/generate-ticket', '/ticket-counter'];
      return ticketOperatorAllowedRoutes.some(allowedRoute => route.startsWith(allowedRoute)) || route === '/';
    }

    // Event Manager (standalone)
    if (isEventManager && !isTicketManager) {
      const eventManagerAllowedRoutes = [
        '/program-management', '/events', '/venue-management',
        '/media-configuration','/content-editor'
      ];
      return eventManagerAllowedRoutes.some(allowedRoute => route.startsWith(allowedRoute));
    }

    return false;
  }

  /**
   * Set the last known role and persist it to sessionStorage
   */
  private setLastKnownRole(role: string): void {
    this.lastKnownRole = role;
    sessionStorage.setItem(this.ROLE_STORAGE_KEY, role);
  }

  /**
   * Clear the cached role data (e.g., on logout)
   */
  clearRole(): void {
    this.lastKnownRole = null;
    this.isRedirecting = false;
    sessionStorage.removeItem(this.ROLE_STORAGE_KEY);
  }

  /**
   * Force refresh of cached role data
   */
  async refreshRole(): Promise<string> {
    const freshRole = await this.cognitoService.getCurrentUserRoles();
    this.setLastKnownRole(freshRole);
    return freshRole;
  }

  /**
   * Get current cached role
   */
  getCurrentCachedRole(): string | null {
    return this.lastKnownRole;
  }
}